/**
 * Reading a screen's view out of the address. A feature's parser takes the
 * raw search and keeps what it knows, in the shapes it allows; whatever it
 * does not accept is dropped, never trusted, and never sent to a service.
 *
 * A route hands its parser to the router through `strictSearch`, and the
 * shell writes the address over with what the parser kept (`cleanedSearch`),
 * so the address says what the screen shows.
 */

/** Text typed into the address, trimmed; nothing for an empty one. */
export function text(value: unknown): string | undefined {
  // The router reads `q=530118942` as a number.
  const string = typeof value === "number" ? String(value) : value;
  return typeof string === "string" && string.trim() !== "" ? string.trim() : undefined;
}

/** A page after the first: the first is not written. */
export function pageOf(value: unknown): number | undefined {
  const page = Number(value);
  return Number.isInteger(page) && page > 1 ? page : undefined;
}

export const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The id of a thing: a UUID, as every service names things. A service refuses anything else. */
export function idOf(value: unknown): string | undefined {
  const id = text(value);
  return id !== undefined && UUID.test(id) ? id : undefined;
}

/**
 * A code a service names a kind by (`visit_settlement`, `clearing:click`):
 * small letters, digits and `_`, at most one `:`. One a service does not know
 * finds nothing; one of another shape is refused, so it is not sent.
 */
export function codeOf(value: unknown): string | undefined {
  const code = text(value);
  return code !== undefined && /^[a-z][a-z0-9_]*(:[a-z0-9_]+)?$/.test(code) && code.length <= 60
    ? code
    : undefined;
}

/** A market's code: two capitals (`UZ`). */
export function marketOf(value: unknown): string | undefined {
  const code = text(value);
  return code !== undefined && /^[A-Z]{2}$/.test(code) ? code : undefined;
}

/** A currency's code: three capitals (`UZS`). */
export function currencyOf(value: unknown): string | undefined {
  const code = text(value);
  return code !== undefined && /^[A-Z]{3}$/.test(code) ? code : undefined;
}

/** One of the values a list allows, as it is spelled there. */
export function oneOf<Value extends string>(
  known: readonly Value[],
  value: unknown,
): Value | undefined {
  return known.find((each) => each === value);
}

type Raw = Readonly<Record<string, unknown>>;

/**
 * A route's `validateSearch` that keeps what the parser kept and nothing
 * else. The router lays a route's search over the one above it, and the one
 * at the top is the whole address: a value the parser dropped would come
 * back from underneath (a `?state=` it refused, sent all the same). Every key
 * of the address the parser did not keep is set to `undefined` here, which
 * covers it. Only a leaf route may use it: a route below would lose the keys
 * of this one.
 */
export function strictSearch<Search extends object>(
  parse: (raw: Raw) => Search,
): (raw: Raw) => Search {
  return (raw) => {
    const parsed = parse(raw);
    const covered: Record<string, undefined> = {};
    for (const key of Object.keys(raw)) {
      if (!(key in parsed)) covered[key] = undefined;
    }
    return { ...covered, ...parsed };
  };
}

/** The same value as the address spells it: `2` and `"2"` are one. */
function same(written: unknown, kept: unknown): boolean {
  const spelled = (value: unknown) =>
    typeof value === "string" || typeof value === "number" || typeof value === "boolean"
      ? String(value)
      : JSON.stringify(value);
  return spelled(written) === spelled(kept);
}

/**
 * What the address should say once the route's parser has read it, or
 * `null` when it says that already. `written` is the address's own search,
 * `kept` the route's (a key the parser dropped is `undefined` there). A value
 * that stays keeps the address's spelling, so nothing changes but what had to.
 */
export function cleanedSearch(written: Raw, kept: Raw): Record<string, unknown> | null {
  const next: Record<string, unknown> = {};
  let changed = false;
  for (const [key, value] of Object.entries(kept)) {
    if (value === undefined) continue;
    if (key in written && same(written[key], value)) {
      next[key] = written[key];
    } else {
      next[key] = value;
      changed = true;
    }
  }
  for (const key of Object.keys(written)) {
    if (!(key in next)) changed = true;
  }
  return changed ? next : null;
}
